/**
 * useOfflinePhotoQueue — Phase 10: Offline photo uploads
 *
 * When the crew takes a photo with no signal — or with one bar that drops the
 * upload halfway — the photo is kept on the phone in IndexedDB and sent later.
 *
 * Storage: IndexedDB 'kam-offline' / objectStore 'pending-photos'
 * Each record: { jobId, blob, filename, queuedAt, clientId, status, ... }
 *
 * When it sends, and why:
 *   - as soon as the queue is open (so photos taken before the app was closed
 *     go out the next time it is opened, not only when the signal flips),
 *   - on the browser's 'online' event,
 *   - and every 60 seconds, because phones often never fire 'online' when one
 *     bar turns into two.
 * Every job's photos are sent, not just the job on screen — a photo taken at
 * stop 3 must not wait until someone reopens stop 3.
 *
 * Each photo carries a clientId and a status. A photo marked 'uploading' is
 * skipped by every other flush (another tab, the 60 s timer), so the same
 * photo is never sent twice at once. A photo stuck at 'uploading' for more
 * than two minutes — the app was closed mid-send — goes back to 'pending'.
 * The clientId is also sent with the upload so the server can recognise a
 * repeat if it ever starts checking.
 *
 * Nothing here gates job completion. A pending photo uploads before or after
 * the job is finished, the same as a photo taken online.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../services/api';

const DB_NAME = 'kam-offline';
const STORE   = 'pending-photos';
// Must match useOfflineWork / useBidderOffline. Opening at a lower version than
// the one already on the phone throws VersionError, which quietly switched this
// queue off on every phone that had ever queued a finish.
const VERSION = 2;
const STALE_UPLOAD_MS = 2 * 60 * 1000;
const FLUSH_EVERY_MS  = 60 * 1000;

// ── IndexedDB helpers ─────────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { autoIncrement: true });
      if (!db.objectStoreNames.contains('pending-actions')) db.createObjectStore('pending-actions', { autoIncrement: true });
      if (!db.objectStoreNames.contains('day-cache')) db.createObjectStore('day-cache');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror  = () => reject(req.error);
  });
}

export type PhotoStatus = 'pending' | 'uploading' | 'failed';

export interface PendingPhoto {
  key: number;
  jobId: string;
  blob: Blob;
  filename: string;
  queuedAt: number;
  kind?: 'finish' | 'note' | 'upsell';   // note photos go to the crew note, upsell photos to Michael; the rest to the finish gallery
  scope?: 'this' | 'future';
  stage?: 'before' | 'after';     // a one-off job's before / after photo (the bill waits on both)
  clientId?: string;              // older records predate this; one is assigned on first send
  status?: PhotoStatus;           // missing = 'pending'
  statusAt?: number;
  lastError?: string;
}

function getAllPending(db: IDBDatabase): Promise<PendingPhoto[]> {
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const items: PendingPhoto[] = [];
    const req   = store.openCursor();
    req.onsuccess = (e) => {
      const cursor = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
      if (cursor) {
        items.push({ key: cursor.key as number, ...cursor.value });
        cursor.continue();
      } else {
        resolve(items);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

function addPending(db: IDBDatabase, record: Omit<PendingPhoto, 'key'>): Promise<number> {
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add(record);
    req.onsuccess = () => resolve(req.result as number);
    req.onerror   = () => reject(req.error);
  });
}

function removePending(db: IDBDatabase, key: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(key);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

/**
 * Read-modify-write inside one transaction, so two flushes can't both claim
 * the same photo. `change` returns the new record, or null to leave it alone.
 */
function updatePending(
  db: IDBDatabase,
  key: number,
  change: (current: Omit<PendingPhoto, 'key'>) => Omit<PendingPhoto, 'key'> | null,
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get   = store.get(key);
    let changed = false;
    get.onsuccess = () => {
      if (!get.result) return;
      const next = change(get.result);
      if (!next) return;
      changed = true;
      store.put(next, key);
    };
    tx.oncomplete = () => resolve(changed);
    tx.onerror    = () => reject(tx.error);
  });
}

function newClientId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') return (crypto as any).randomUUID();
  } catch { /* fall through */ }
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Worth trying again later? No response at all (dead signal, dropped
 * connection), a timeout, rate limiting, or the server falling over.
 * A 400/403/404/413 is a real refusal and would fail the same way for ever.
 * Accepts an axios error or a fetch status.
 */
export function isRetryableUploadError(err: any): boolean {
  if (typeof err === 'number') return err >= 500 || err === 408 || err === 429;
  const status = err?.response?.status;
  if (!status) return true;                         // network error — no reply came back
  return status >= 500 || status === 408 || status === 429;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseOfflinePhotoQueueOptions {
  jobId: string;
  onUploaded: (jobId: string) => void; // called after each successful flush upload
}

type EnqueueOpts = { kind: 'note'; scope: 'this' | 'future' } | { kind: 'upsell' } | { kind: 'finish'; stage: 'before' | 'after' };

export function useOfflinePhotoQueue({ jobId, onUploaded }: UseOfflinePhotoQueueOptions) {
  const dbRef                   = useRef<IDBDatabase | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [allPending, setAll]    = useState<PendingPhoto[]>([]);
  const flushingRef             = useRef(false);
  const onUploadedRef           = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const refreshPending = useCallback(async (db: IDBDatabase) => {
    try { setAll(await getAllPending(db)); } catch { /* storage unavailable */ }
  }, []);

  /** Send every job's queued photos. Stops at the first sign the signal is gone. */
  const flush = useCallback(async (opts?: { includeFailed?: boolean; onlyKey?: number }) => {
    const db = dbRef.current;
    if (!db || flushingRef.current || !navigator.onLine) return;
    flushingRef.current = true;
    let sent = 0;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;                            // signed out — keep everything for after login

      const now = Date.now();
      const all = await getAllPending(db);
      const due = all.filter(p => {
        if (opts?.onlyKey != null && p.key !== opts.onlyKey) return false;
        const st = p.status || 'pending';
        if (st === 'pending') return true;
        if (st === 'failed') return !!opts?.includeFailed;
        return now - (p.statusAt || 0) > STALE_UPLOAD_MS; // 'uploading' left behind by a closed app
      });

      for (const item of due) {
        // Claim it. If another flush got there first, leave it to that one.
        const clientId = item.clientId || newClientId();
        const claimed = await updatePending(db, item.key, cur => {
          const st = cur.status || 'pending';
          if (st === 'uploading' && Date.now() - (cur.statusAt || 0) <= STALE_UPLOAD_MS) return null;
          return { ...cur, clientId: cur.clientId || clientId, status: 'uploading', statusAt: Date.now() };
        });
        if (!claimed) continue;
        await refreshPending(db);

        let res: Response | null = null;
        try {
          const form = new FormData();
          form.append('photo', item.blob, item.filename);
          form.append('client_id', item.clientId || clientId);
          if (item.kind === 'note') form.append('scope', item.scope || 'this');
          if (item.stage) form.append('stage', item.stage);

          // Same absolute API base as online uploads — a relative /api hits the
          // static host (Netlify) and 404s, silently eating field photos.
          const path = item.kind === 'note' ? 'crew-notes/photos' : item.kind === 'upsell' ? 'upsell-photo' : 'photos';
          res = await fetch(`${API_BASE_URL}/jobs/${item.jobId}/${path}`, {
            method:  'POST',
            headers: { Authorization: `Bearer ${token}` },
            body:    form,
          });
        } catch (err) {
          // No reply at all — still out of range. Put it back and try later.
          console.warn('Offline photo upload retry failed:', err);
          await updatePending(db, item.key, cur => ({ ...cur, status: 'pending', statusAt: Date.now() }));
          break;
        }

        if (res.ok) {
          await removePending(db, item.key);
          sent++;
          onUploadedRef.current(String(item.jobId));
          continue;
        }

        if (res.status === 401 || isRetryableUploadError(res.status)) {
          // 401: the login expired — keep the photo for after they sign back in.
          // 5xx / 408 / 429: the server or the signal is struggling. Either way,
          // stop here and try the whole queue again later.
          await updatePending(db, item.key, cur => ({ ...cur, status: 'pending', statusAt: Date.now() }));
          break;
        }

        // A real refusal (too big, job gone, not this crew's job). Retrying on a
        // timer would fail the same way for ever, so park it where the crew can
        // see it and choose to retry or discard.
        let reason = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.message) reason = j.message; } catch { /* not JSON */ }
        await updatePending(db, item.key, cur => ({ ...cur, status: 'failed', statusAt: Date.now(), lastError: reason }));
      }
    } catch (err) {
      console.warn('Offline photo flush failed:', err);
    } finally {
      flushingRef.current = false;
      await refreshPending(db);
      if (sent) toast.success(`${sent} saved photo${sent === 1 ? '' : 's'} uploaded`, { autoClose: 3000 });
    }
  }, [refreshPending]);

  // ── Init IndexedDB, then send whatever was left from last time ─────────────
  useEffect(() => {
    let mounted = true;
    openDB()
      .then(async db => {
        if (!mounted) { db.close(); return; }
        dbRef.current = db;
        await refreshPending(db);
        if (navigator.onLine) flush();
      })
      .catch(err => console.warn('IndexedDB unavailable — offline photo queue disabled', err));

    return () => {
      mounted = false;
      dbRef.current?.close();
      dbRef.current = null;
    };
  }, [flush, refreshPending]);

  // ── Online/offline tracking, plus a slow timer for signal that creeps back ─
  useEffect(() => {
    const onOnline  = () => { setIsOnline(true); flush(); };
    const onOffline = () => { setIsOnline(false); };
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    const t = setInterval(() => { if (navigator.onLine) flush(); }, FLUSH_EVERY_MS);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(t);
    };
  }, [flush]);

  // ── Public API ───────────────────────────────────────────────────────────────

  /**
   * Queue a file for upload. Returns whether it was stored on the phone (true)
   * or should be uploaded now by the caller (false).
   *
   * `force` stores it even when the phone thinks it is online — use it after an
   * online upload failed with a network error or a 5xx, so a weak-signal photo
   * is kept rather than lost.
   */
  const enqueue = useCallback(async (file: File, opts?: EnqueueOpts, force?: boolean): Promise<boolean> => {
    if (!dbRef.current) return false; // IDB not available — caller handles it
    if (navigator.onLine && !force) return false; // Online — caller should upload directly

    try {
      await addPending(dbRef.current, {
        jobId,
        blob:      file,
        filename:  file.name,
        queuedAt:  Date.now(),
        clientId:  newClientId(),
        status:    'pending',
        statusAt:  Date.now(),
        ...(opts || {}),
      });
    } catch {
      return false;                    // phone storage full or blocked — let the caller report it
    }
    await refreshPending(dbRef.current);
    toast.info(force
      ? 'Upload didn’t go through — photo saved on the phone. It will keep trying.'
      : 'No signal — photo saved locally. Will upload when you’re back online.', { autoClose: 6000 });
    return true; // stored offline
  }, [jobId, refreshPending]);

  /** Try one parked photo again (the crew tapped Retry). */
  const retry = useCallback(async (key: number) => {
    const db = dbRef.current;
    if (!db) return;
    await updatePending(db, key, cur => ({ ...cur, status: 'pending', statusAt: Date.now(), lastError: undefined }));
    await refreshPending(db);
    if (navigator.onLine) flush({ includeFailed: true, onlyKey: key });
  }, [flush, refreshPending]);

  /** Throw away a photo the server keeps refusing. */
  const discard = useCallback(async (key: number) => {
    const db = dbRef.current;
    if (!db) return;
    await removePending(db, key);
    await refreshPending(db);
  }, [refreshPending]);

  // The screen shows this job's photos; the queue sends everyone's.
  const pending = allPending.filter(p => String(p.jobId) === String(jobId));

  return {
    isOnline,
    pendingCount: pending.length,
    pending,
    totalPendingCount: allPending.length,
    enqueue,
    retry,
    discard,
    flush,
  };
}
